import { Component, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Task } from '../../../../types/task.interface';
import {
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from '@angular/forms';
import { TaskResult } from '../../../../types/task-result.interface';
import { CamundaService } from '../../../../service/camunda.service';
import { Store } from '@ngrx/store';
import { FluxBPMState } from '../../../../store/reducer/flux-bpm.reducers';
import { UserInput } from '../../../../types/user-input.interface';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { AbstractTemplateComponent } from '../../abstract/abstract-template.component';
import { NgForOf, NgIf, NgSwitch, NgSwitchCase } from '@angular/common';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { SensorComponent } from '../../sensor-component/sensor.component';
import { ImageComponent } from '../../image-component/image.component';
import { Util } from '@flux/flux-core';

@Component({
  selector: 'lib-flux-user-input-step-template',
  templateUrl: './user-input-template.component.html',
  styleUrls: ['./user-input-template.component.scss'],
  imports: [
    ReactiveFormsModule,
    NgSwitch,
    NgForOf,
    NgSwitchCase,
    NgIf,
    TranslateDirective,
    SensorComponent,
    ImageComponent,
    TranslatePipe,
  ],
  standalone: true,
})
export class UserInputTemplateComponent
  extends AbstractTemplateComponent
  implements OnInit, OnDestroy
{
  currentTask: Task;
  form: UntypedFormGroup;
  textInputs: UserInput[] = [];

  sensorsExpanded = false;

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    switch (event.key) {
      case '%':
        this.finishTask('Weiter', 'yes');
        break;
      case '§':
        this.finishTask('Zurück', 'back');
        break;
    }
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    protected camundaService: CamundaService,
    protected store: Store<FluxBPMState>,
    private doms: DomSanitizer,
  ) {
    super(camundaService, store);
  }

  public taskUpdated(): void {
    if (this.form) {
      // Reset all form controls
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key).setValue('');
      });
    }
  }

  getFormControl(controlName: string): UntypedFormControl {
    return this.form.get(controlName) as UntypedFormControl;
  }

  safeCss(style): SafeStyle {
    if (Util.checkIfNull(style)) {
      return;
    }
    if (style.trim().length > 0) {
      return this.doms.bypassSecurityTrustStyle(style);
    }
  }

  toggleFullscreen(): void {
    // Implementation for toggleFullscreen
  }

  toggleSensors(): void {
    this.sensorsExpanded = !this.sensorsExpanded;
  }

  ngOnInit(): void {
    // Erstelle ein leeres FormGroup
    this.form = this.formBuilder.group({});

    // Extrahiere alle Textfeld-Eingaben
    this.textInputs =
      this.currentTask?.userInputs?.filter(
        (input) => input.inputType === 'Textfield',
      ) || [];

    // Füge für jedes Textfeld ein FormControl hinzu
    this.textInputs.forEach((input, index) => {
      const controlName = `input_${index}`;
      const validators = [Validators.required];

      // Regex-Validatoren hinzufügen, wenn verfügbar
      this.currentTask.patternMappingVariables?.forEach((pattern) => {
        validators.push(
          this.regexValidator(new RegExp(pattern.pattern), {
            pattern: pattern.failureMessage,
          }),
        );
      });

      this.form.addControl(
        controlName,
        this.formBuilder.control('', validators),
      );
    });

    // Wenn keine Textfelder vorhanden sind, ein Dummy-Control erstellen für Abwärtskompatibilität
    if (this.textInputs.length === 0) {
      this.form.addControl('inputComponent', this.formBuilder.control(''));
    }

    super.onInit(false);
  }

  regexValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: UntypedFormControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const valid = regex.test(control.value);
      return valid ? null : error;
    };
  }

  hasSideContent(): boolean {
    return (
      this.currentTask?.images?.length > 0 ||
      this.currentTask?.sensors?.length > 0
    );
  }

  getErrorKeys(controlName: string): string[] {
    const control = this.form.get(controlName);
    return control && control.errors ? Object.keys(control.errors) : [];
  }

  submitTask(userInput: UserInput): void {
    this.finishTask(userInput.name, userInput.value);
  }

  nextTriggered(): void {
    if (this.form.valid) {
      // Für den Fall eines einzelnen Textfelds, behalten wir das alte Verhalten bei
      if (this.textInputs.length === 1) {
        const controlName = `input_0`;
        this.finishTask('', this.form.get(controlName).value);
      }
      // Für mehrere Textfelder, sammeln wir alle Werte
      else if (this.textInputs.length > 1) {
        const allValues = {};

        // Alle Werte sammeln mit dem Namen des Textfelds als Schlüssel
        this.textInputs.forEach((input, index) => {
          const controlName = `input_${index}`;
          allValues[input.name] = this.form.get(controlName).value;
        });

        // Als JSON-String senden
        this.finishTask('', JSON.stringify(allValues));
      }
    }
  }
}
